import { FormEvent, KeyboardEvent, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Switch,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";
import AttachFile from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import {
  AddWhiteLabelData,
  createAddWhiteLabel,
  getMyAddWhiteLabel,
  updateMyAddWhiteLabel,
} from "../../features/add-white-label-module/addWhiteLabelAction";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(-1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: 22,
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: theme.spacing(1),
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #455354)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1, 5),
  fontSize: "16px",
  "&:hover": {
    background: "linear-gradient(to top, #758d8e, #455354)",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    backgroundColor: "#b5b4b1",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
  },
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      color: "#323c3d",
      "& + .MuiSwitch-track": {
        backgroundColor: "#323c3d",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
    backgroundColor: "#ffffff",
  },
}));

const AddWhiteLabels = () => {
  const [formData, setFormData] = useState({
    brandColor: "",
    textColor: "",
    bgColor: "",
    domainURL: "",
    termsOfServiceURL: "",
    privacyPolicyURL: "",
  });
  const [toggles, setToggles] = useState({
    termsEnabled: true,
    privacyEnabled: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [logoName, setLogoName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [errors, setErrors] = useState({
    logo: "",
    brandColor: "",
    textColor: "",
    bgColor: "",
    domainURL: "",
    termsOfServiceURL: "",
    privacyPolicyURL: "",
  });
  const [touched, setTouched] = useState({
    logo: false,
    brandColor: false,
    textColor: false,
    bgColor: false,
    domainURL: false,
    termsOfServiceURL: false,
    privacyPolicyURL: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, whiteLabel } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isUpdate = !!whiteLabel;

  useEffect(() => {
    dispatch(getMyAddWhiteLabel());
  }, [dispatch]);

  useEffect(() => {
    if (whiteLabel) {
      setFormData({
        brandColor: whiteLabel.brandColor || "",
        textColor: whiteLabel.textColor || "",
        bgColor: whiteLabel.bgColor || "",
        domainURL: whiteLabel.domainURL || "",
        termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
        privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
      });
      setToggles({
        termsEnabled: !!whiteLabel.termsOfServiceURL,
        privacyEnabled: !!whiteLabel.privacyPolicyURL,
      });
      setLogoBase64(whiteLabel.brandLogo || null);
      if (whiteLabel.brandLogo) {
        setPreviewUrl(whiteLabel.brandLogo);
        setLogoName("Current Logo");
        setImageDimensions(null);
      }
      setErrors({
        logo: "",
        brandColor: "",
        textColor: "",
        bgColor: "",
        domainURL: "",
        termsOfServiceURL: "",
        privacyPolicyURL: "",
      });
      setTouched({
        logo: false,
        brandColor: false,
        textColor: false,
        bgColor: false,
        domainURL: false,
        termsOfServiceURL: false,
        privacyPolicyURL: false,
      });
    }
  }, [whiteLabel]);

  const validateField = (name: string, value: string) => {
    const trimmedValue = value.trim();
    switch (name) {
      case "brandColor":
        if (!trimmedValue) return "Brand color is required";
        if (!/^#[0-9A-Fa-f]{6}$/.test(trimmedValue))
          return "Enter a valid 6-digit hex color (e.g., #RRGGBB)";
        return "";
      case "textColor":
        if (!trimmedValue) return "Text color is required";
        if (!/^#[0-9A-Fa-f]{6}$/.test(trimmedValue))
          return "Enter a valid 6-digit hex color (e.g., #RRGGBB)";
        return "";
      case "bgColor":
        if (!trimmedValue) return "Background color is required";
        if (!/^#[0-9A-Fa-f]{6}$/.test(trimmedValue))
          return "Enter a valid 6-digit hex color (e.g., #RRGGBB)";
        return "";
      case "domainURL":
        if (!trimmedValue) return "Domain URL is required";
        if (!/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/.test(trimmedValue))
          return "Enter a valid URL (e.g., https://example.com)";
        return "";
      case "termsOfServiceURL":
        if (!trimmedValue && toggles.termsEnabled)
          return "Terms of Service URL is required";
        if (
          trimmedValue &&
          !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/.test(trimmedValue)
        )
          return "Enter a valid URL (e.g., https://example.com/terms)";
        return "";
      case "privacyPolicyURL":
        if (!trimmedValue && toggles.privacyEnabled)
          return "Privacy Policy URL is required";
        if (
          trimmedValue &&
          !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/.test(trimmedValue)
        )
          return "Enter a valid URL (e.g., https://example.com/privacy)";
        return "";
      default:
        return "";
    }
  };

  const validateLogo = (): string => {
    if (logoFile) {
      return "";
    }
    if (isUpdate && whiteLabel?.brandLogo) {
      return "";
    }
    return "Logo is required";
  };

  const validateImage = (file: File | null): string => {
    if (!file) return "";
    if (file.size > 10 * 1024 * 1024) return "Image must be under 10MB";
    if (!["image/png"].includes(file.type)) return "Image must be PNG";
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setFormData((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));

    if (
      (name === "termsOfServiceURL" && !toggles.termsEnabled) ||
      (name === "privacyPolicyURL" && !toggles.privacyEnabled)
    ) {
      return;
    }

    if (touched[name as keyof typeof touched]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, trimmedValue),
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<{ name: string }>) => {
    const { name } = e.target;
    if (
      (name === "termsOfServiceURL" && !toggles.termsEnabled) ||
      (name === "privacyPolicyURL" && !toggles.privacyEnabled)
    ) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
      return;
    }
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, formData[name as keyof typeof formData]),
    }));
  };

  const handleToggleChange = (name: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setToggles((prev) => ({
      ...prev,
      [name]: checked,
    }));
    const fieldName =
      name === "termsEnabled" ? "termsOfServiceURL" : "privacyPolicyURL";
    if (!checked) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
      setTouched((prev) => ({
        ...prev,
        [fieldName]: false,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: validateField(
          fieldName,
          formData[fieldName as keyof typeof formData]
        ),
      }));
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          const base64Length = reader.result.length;
          const maxBase64Size = (10 * 1024 * 1024 * 4) / 3;
          if (base64Length > maxBase64Size) {
            reject(new Error("Base64-encoded image exceeds 10MB limit"));
          } else {
            resolve(reader.result);
          }
        } else {
          reject(new Error("Failed to convert image to base64"));
        }
      };
      reader.onerror = () => reject(new Error("Error reading image"));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setTouched((prev) => ({ ...prev, logo: true }));
    if (file) {
      const imageError = validateImage(file);
      setErrors((prev) => ({
        ...prev,
        logo: imageError,
      }));
      if (imageError) {
        toast.error(imageError);
        setLogoFile(null);
        setLogoBase64(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      try {
        const base64Image = await convertToBase64(file);
        setLogoBase64(base64Image);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setLogoName(file.name);
        setImageDimensions(null);
        setLogoFile(file);
        setErrors((prev) => ({ ...prev, logo: "" }));
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to process image";
        setErrors((prev) => ({
          ...prev,
          logo: errorMessage,
        }));
        toast.error(errorMessage);
        setLogoFile(null);
        setLogoBase64(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } else {
      setLogoFile(null);
      setLogoBase64(isUpdate ? whiteLabel?.brandLogo || null : null);
      setPreviewUrl(isUpdate ? whiteLabel?.brandLogo || null : null);
      setLogoName(isUpdate ? "Current Logo" : "");
      setErrors((prev) => ({ ...prev, logo: validateLogo() }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors = {
      logo: validateLogo(),
      brandColor: validateField("brandColor", formData.brandColor),
      textColor: validateField("textColor", formData.textColor),
      bgColor: validateField("bgColor", formData.bgColor),
      domainURL: validateField("domainURL", formData.domainURL),
      termsOfServiceURL: toggles.termsEnabled
        ? validateField("termsOfServiceURL", formData.termsOfServiceURL)
        : "",
      privacyPolicyURL: toggles.privacyEnabled
        ? validateField("privacyPolicyURL", formData.privacyPolicyURL)
        : "",
    };

    setErrors(newErrors);
    setTouched({
      logo: true,
      brandColor: true,
      textColor: true,
      bgColor: true,
      domainURL: true,
      termsOfServiceURL: true,
      privacyPolicyURL: true,
    });

    const hasError = Object.values(newErrors).some((msg) => msg);
    if (hasError) {
      toast.error("Please fix the errors in the form.");
      return;
    }

    try {
      const payload: Partial<AddWhiteLabelData> = {
        brandColor: formData.brandColor,
        textColor: formData.textColor,
        bgColor: formData.bgColor,
        domainURL: formData.domainURL,
        termsOfServiceURL: toggles.termsEnabled
          ? formData.termsOfServiceURL
          : "",
        privacyPolicyURL: toggles.privacyEnabled
          ? formData.privacyPolicyURL
          : "",
      };

      if (logoFile) {
        payload.brandLogo = logoBase64 as string;
      }

      if (isUpdate) {
        await dispatch(updateMyAddWhiteLabel(payload));
      } else {
        await dispatch(createAddWhiteLabel(payload as AddWhiteLabelData));
      }
    } catch (error) {
      toast.error("Operation failed. Please try again.");
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  const handleClearImage = () => {
    setLogoFile(null);
    setLogoBase64(isUpdate ? whiteLabel?.brandLogo || null : null);
    setPreviewUrl(isUpdate ? whiteLabel?.brandLogo || null : null);
    setLogoName(isUpdate ? "Current Logo" : "");
    setImageDimensions(null);
    setErrors((prev) => ({ ...prev, logo: validateLogo() }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const effectiveErrors = {
    ...errors,
    termsOfServiceURL: toggles.termsEnabled ? errors.termsOfServiceURL : "",
    privacyPolicyURL: toggles.privacyEnabled ? errors.privacyPolicyURL : "",
  };
  const isFormValid =
    (logoFile || (isUpdate && whiteLabel?.brandLogo)) &&
    Boolean(formData.brandColor) &&
    Boolean(formData.textColor) &&
    Boolean(formData.bgColor) &&
    Boolean(formData.domainURL) &&
    (toggles.termsEnabled ? Boolean(formData.termsOfServiceURL) : true) &&
    (toggles.privacyEnabled ? Boolean(formData.privacyPolicyURL) : true) &&
    !Object.values(effectiveErrors).some((err) => err);

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
          >
            <WelcomeText>White Labels</WelcomeText>
            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: isMobile ? 2 : 2.5,
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "#4b5b5d",
                  fontWeight: 600,
                  mb: 1,
                  alignSelf: "center",
                }}
              >
                {isUpdate ? "Update White Labels" : "Add White Labels"}
              </Typography>
              <Grid container spacing={2} sx={{ width: "100%", mb: 2 }}>
                <SubHeading>Branding</SubHeading>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Upload Your Logo
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <StyledTextField
                      value={logoName}
                      disabled
                      placeholder="File"
                      sx={{ flex: 1, mr: 1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {logoFile && (
                              <IconButton
                                aria-label="clear image"
                                onClick={handleClearImage}
                                sx={{ p: 1 }}
                              >
                                <ClearIcon />
                              </IconButton>
                            )}
                            <Button
                              component="label"
                              sx={{ minWidth: "auto", p: 1 }}
                            >
                              <AttachFile />
                              <input
                                type="file"
                                hidden
                                onChange={handleFileChange}
                                accept="image/png,image/jpeg"
                                ref={fileInputRef}
                              />
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: "#757575" }}>
                    (Recommended: PNG, under 10 MB)
                  </Typography>
                  {errors.logo && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {errors.logo}
                    </Typography>
                  )}
                  {previewUrl && (
                    <>
                      <Box
                        sx={{
                          mt: 1,
                          position: "relative",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: { xs: 200, sm: 260 },
                          height: { xs: 130, sm: 160 },
                          mx: "auto",
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                          overflow: "hidden",
                          p: 1,
                          backgroundImage:
                            "linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)",
                          backgroundSize: "20px 20px",
                          backgroundPosition:
                            "0 0, 0 10px, 10px -10px, -10px 0px",
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Logo Preview"
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            if (logoFile) {
                              setImageDimensions({
                                width: img.naturalWidth,
                                height: img.naturalHeight,
                              });
                            }
                          }}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = "scale(1.02)";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.75,
                          display: "block",
                          textAlign: "center",
                          color: "#616161",
                        }}
                      >
                        {logoName}
                        {logoFile
                          ? ` • ${(logoFile.size / 1024).toFixed(1)} KB`
                          : ""}
                        {imageDimensions
                          ? ` • ${imageDimensions.width}×${imageDimensions.height}px`
                          : ""}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Enter Brand Color
                  </Typography>
                  <StyledTextField
                    name="brandColor"
                    placeholder="Hex"
                    variant="outlined"
                    value={formData.brandColor}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.brandColor}
                    helperText={errors.brandColor}
                    required
                    size={isMobile ? "small" : "medium"}
                    sx={{ mb: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Enter Text Color
                  </Typography>
                  <StyledTextField
                    name="textColor"
                    placeholder="Hex"
                    variant="outlined"
                    value={formData.textColor}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.textColor}
                    helperText={errors.textColor}
                    required
                    size={isMobile ? "small" : "medium"}
                    sx={{ mb: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Enter Background Color
                  </Typography>
                  <StyledTextField
                    name="bgColor"
                    placeholder="Hex"
                    variant="outlined"
                    value={formData.bgColor}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.bgColor}
                    helperText={errors.bgColor}
                    required
                    size={isMobile ? "small" : "medium"}
                    sx={{ mb: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SubHeading>Domain</SubHeading>
                  <StyledTextField
                    name="domainURL"
                    placeholder="https://yourwebsite.com"
                    variant="outlined"
                    value={formData.domainURL}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.domainURL}
                    helperText={errors.domainURL}
                    required
                    size={isMobile ? "small" : "medium"}
                    sx={{ mb: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SubHeading>Others</SubHeading>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Terms of Service
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <StyledTextField
                      name="termsOfServiceURL"
                      placeholder="https://yourwebsite.com/terms-of-service"
                      variant="outlined"
                      value={formData.termsOfServiceURL}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.termsOfServiceURL}
                      helperText={errors.termsOfServiceURL}
                      disabled={!toggles.termsEnabled}
                      size={isMobile ? "small" : "medium"}
                      sx={{ flex: 1, mr: 1, mb: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Android12Switch
                              checked={toggles.termsEnabled}
                              onChange={handleToggleChange("termsEnabled")}
                              size={isMobile ? "small" : "medium"}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#4b5b5d", fontWeight: 600, mb: 0.5 }}
                  >
                    Privacy Policy
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <StyledTextField
                      name="privacyPolicyURL"
                      placeholder="https://yourwebsite.com/privacy-policy"
                      variant="outlined"
                      value={formData.privacyPolicyURL}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.privacyPolicyURL}
                      helperText={errors.privacyPolicyURL}
                      disabled={!toggles.privacyEnabled}
                      size={isMobile ? "small" : "medium"}
                      sx={{ flex: 1, mr: 1, mb: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Android12Switch
                              checked={toggles.privacyEnabled}
                              onChange={handleToggleChange("privacyEnabled")}
                              size={isMobile ? "small" : "medium"}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                sx={{ width: "100%", justifyContent: "flex-end", marginTop: 5 }}
              >
                <Grid
                  size={{ xs: 12, sm: 6 }}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <motion.div
                    whileHover={{
                      scale: 1.04,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    style={{ width: "100%", maxWidth: "300px" }}
                  >
                    <PrimaryButton
                      type="submit"
                      variant="contained"
                      disabled={loading || !isFormValid}
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {loading
                        ? isUpdate
                          ? "Updating..."
                          : "Adding..."
                        : isUpdate
                        ? "Update White Label"
                        : "Add White Label"}
                    </PrimaryButton>
                  </motion.div>
                </Grid>
              </Grid>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default AddWhiteLabels;
